import { useEffect, useState } from 'react';
import { ApiError, apiGet, apiPatch, apiPost } from '../../api/client.js';
import { PlusIcon, Trash2Icon, XIcon } from '../icons.js';

/**
 * Admin modal — set library-facing metadata on a video after ingest.
 *
 * Hits `PATCH /api/v1/videos/:id/metadata` with only the keys the operator
 * touched. The server merges; never overwrites unrelated fields.
 *
 * Series picker reads `GET /api/v1/video-series`. A "Create new series"
 * row sits at the bottom of the dropdown so the operator doesn't have to
 * leave the page to seed one — keeps the workflow self-contained.
 *
 * Resources editor:
 *   - kind: 'doc' (links to /documents?id=…) | 'link' (external URL or internal route)
 *   - 20-resource cap matches the shared Zod schema.
 */

interface VideoMetadata {
  id: string;
  title?: string;
  seriesId?: string;
  episode?: number;
  totalEpisodes?: number;
  isFeatured?: boolean;
  presenter?: { name: string; role?: string };
  resources?: { kind: 'doc' | 'link'; title: string; documentId?: string; href?: string }[];
  publishedAt?: string;
}

interface VideoSeries {
  id: string;
  title: string;
  subtitle?: string;
}

interface Props {
  video: VideoMetadata;
  onClose: () => void;
  /** Called after a successful save so the parent list can refresh. */
  onSaved: () => void;
}

export function EditVideoMetadataModal({ video, onClose, onSaved }: Props): JSX.Element {
  const [seriesList, setSeriesList] = useState<VideoSeries[]>([]);
  const [title, setTitle] = useState(video.title ?? '');
  const [seriesId, setSeriesId] = useState<string>(video.seriesId ?? '');
  const [episode, setEpisode] = useState<string>(video.episode ? String(video.episode) : '');
  const [totalEpisodes, setTotalEpisodes] = useState<string>(
    video.totalEpisodes ? String(video.totalEpisodes) : '',
  );
  const [isFeatured, setIsFeatured] = useState<boolean>(video.isFeatured ?? false);
  const [presenterName, setPresenterName] = useState<string>(video.presenter?.name ?? '');
  const [presenterRole, setPresenterRole] = useState<string>(video.presenter?.role ?? '');
  const [resources, setResources] = useState<VideoMetadata['resources']>(video.resources ?? []);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creatingSeries, setCreatingSeries] = useState(false);
  const [newSeriesTitle, setNewSeriesTitle] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet<{ series: VideoSeries[] }>('/api/v1/video-series');
        setSeriesList(res.series);
      } catch {
        // non-fatal — user can still save without picking a series
      }
    })();
  }, []);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (title.trim() && title.trim() !== (video.title ?? '')) body.title = title.trim();
      if (seriesId !== (video.seriesId ?? '')) body.seriesId = seriesId || null;
      if (episode !== String(video.episode ?? '')) {
        const n = Number.parseInt(episode, 10);
        body.episode = Number.isFinite(n) && n > 0 ? n : null;
      }
      if (totalEpisodes !== String(video.totalEpisodes ?? '')) {
        const n = Number.parseInt(totalEpisodes, 10);
        body.totalEpisodes = Number.isFinite(n) && n > 0 ? n : null;
      }
      if (isFeatured !== (video.isFeatured ?? false)) body.isFeatured = isFeatured;
      if (presenterName.trim() && presenterName.trim() !== (video.presenter?.name ?? '')) {
        body.presenter = { name: presenterName.trim(), role: presenterRole.trim() || undefined };
      } else if (presenterRole.trim() && presenterRole.trim() !== (video.presenter?.role ?? '')) {
        body.presenter = {
          name: presenterName.trim() || video.presenter?.name || 'Unknown',
          role: presenterRole.trim(),
        };
      }
      // Resources: always send the current array — easier than diffing nested.
      if (JSON.stringify(resources ?? []) !== JSON.stringify(video.resources ?? [])) {
        body.resources = resources ?? [];
      }
      // Backend rejects empty PATCH; bail out client-side.
      if (Object.keys(body).length === 0) {
        setError('No changes to save.');
        setSaving(false);
        return;
      }
      await apiPatch(`/api/v1/videos/${video.id}/metadata`, body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const createSeries = async (): Promise<void> => {
    if (!newSeriesTitle.trim()) return;
    try {
      const created = await apiPost<VideoSeries>('/api/v1/video-series', {
        title: newSeriesTitle.trim(),
      });
      setSeriesList((prev) => [...prev, created]);
      setSeriesId(created.id);
      setCreatingSeries(false);
      setNewSeriesTitle('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create series');
    }
  };

  const addResource = (): void => {
    if ((resources?.length ?? 0) >= 20) return;
    setResources([...(resources ?? []), { kind: 'link', title: '', href: '' }]);
  };

  const updateResource = (
    i: number,
    patch: Partial<NonNullable<VideoMetadata['resources']>[number]>,
  ): void => {
    setResources((prev) => (prev ?? []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const removeResource = (i: number): void => {
    setResources((prev) => (prev ?? []).filter((_, idx) => idx !== i));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-nebula-border px-5 py-3.5">
          <div>
            <h2 className="text-base font-semibold text-nebula-fg-1">Edit video metadata</h2>
            <p className="font-mono text-xs text-nebula-fg-3">{video.id}</p>
          </div>
          <button onClick={onClose} className="text-nebula-fg-2 hover:text-nebula-fg-1">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          {error && (
            <div className="rounded-md border border-nebula-error bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
              {error}
            </div>
          )}

          <Field label="Title">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-nebula-border px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Series">
              <select
                value={seriesId}
                onChange={(e) => {
                  if (e.target.value === '__new__') setCreatingSeries(true);
                  else setSeriesId(e.target.value);
                }}
                className="w-full rounded-md border border-nebula-border bg-white px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
              >
                <option value="">— No series —</option>
                {seriesList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
                <option value="__new__">+ Create new series…</option>
              </select>
              {creatingSeries && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    placeholder="New series title…"
                    value={newSeriesTitle}
                    onChange={(e) => setNewSeriesTitle(e.target.value)}
                    autoFocus
                    className="flex-1 rounded-md border border-nebula-border px-3 py-1.5 text-sm focus:border-nebula-secondary focus:outline-none"
                  />
                  <button
                    onClick={() => void createSeries()}
                    disabled={!newSeriesTitle.trim()}
                    className="rounded-md bg-nebula-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => {
                      setCreatingSeries(false);
                      setNewSeriesTitle('');
                    }}
                    className="rounded-md border border-nebula-border px-3 py-1.5 text-xs text-nebula-fg-2 hover:bg-nebula-tint"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Episode #">
                <input
                  type="number"
                  min={1}
                  value={episode}
                  onChange={(e) => setEpisode(e.target.value)}
                  disabled={!seriesId}
                  className="w-full rounded-md border border-nebula-border px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none disabled:bg-nebula-gray-5 disabled:opacity-50"
                />
              </Field>
              <Field label="Total episodes">
                <input
                  type="number"
                  min={1}
                  value={totalEpisodes}
                  onChange={(e) => setTotalEpisodes(e.target.value)}
                  disabled={!seriesId}
                  className="w-full rounded-md border border-nebula-border px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none disabled:bg-nebula-gray-5 disabled:opacity-50"
                />
              </Field>
            </div>
          </div>

          <Field label="Presenter">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="Name"
                value={presenterName}
                onChange={(e) => setPresenterName(e.target.value)}
                className="rounded-md border border-nebula-border px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
              />
              <input
                type="text"
                placeholder="Role / title"
                value={presenterRole}
                onChange={(e) => setPresenterRole(e.target.value)}
                className="rounded-md border border-nebula-border px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
              />
            </div>
          </Field>

          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-nebula-border accent-nebula-primary"
            />
            <div>
              <div className="text-sm font-semibold text-nebula-fg-1">Featured on the library</div>
              <div className="text-xs text-nebula-fg-3">
                Only one video can be featured at a time; setting this clears it from any other
                video.
              </div>
            </div>
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-4">
                Resources ({resources?.length ?? 0} / 20)
              </div>
              <button
                onClick={addResource}
                disabled={(resources?.length ?? 0) >= 20}
                className="inline-flex items-center gap-1 rounded-md border border-nebula-border bg-white px-2.5 py-1 text-xs font-semibold text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-40"
              >
                <PlusIcon className="w-3 h-3" />
                Add
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {(resources ?? []).map((r, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[80px_1fr_1fr_28px] gap-2 rounded-md border border-nebula-border-soft p-2"
                >
                  <select
                    value={r.kind}
                    onChange={(e) => updateResource(i, { kind: e.target.value as 'doc' | 'link' })}
                    className="rounded border border-nebula-border bg-white px-2 py-1 text-xs"
                  >
                    <option value="link">Link</option>
                    <option value="doc">Document</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Title"
                    value={r.title}
                    onChange={(e) => updateResource(i, { title: e.target.value })}
                    className="rounded border border-nebula-border px-2 py-1 text-sm focus:border-nebula-secondary focus:outline-none"
                  />
                  {r.kind === 'doc' ? (
                    <input
                      type="text"
                      placeholder="Document filename"
                      value={r.documentId ?? ''}
                      onChange={(e) =>
                        updateResource(i, { documentId: e.target.value, href: undefined })
                      }
                      className="rounded border border-nebula-border px-2 py-1 text-sm focus:border-nebula-secondary focus:outline-none"
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder="https://… or /path"
                      value={r.href ?? ''}
                      onChange={(e) =>
                        updateResource(i, { href: e.target.value, documentId: undefined })
                      }
                      className="rounded border border-nebula-border px-2 py-1 text-sm focus:border-nebula-secondary focus:outline-none"
                    />
                  )}
                  <button
                    onClick={() => removeResource(i)}
                    title="Remove resource"
                    className="inline-flex items-center justify-center rounded text-nebula-fg-3 hover:bg-nebula-error-bg hover:text-nebula-error"
                  >
                    <Trash2Icon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(resources?.length ?? 0) === 0 && (
                <div className="rounded-md border border-dashed border-nebula-border-soft px-3 py-4 text-center text-xs text-nebula-fg-3">
                  No resources attached. Click "Add" to link a document or external URL.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-nebula-border px-5 py-3.5">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-nebula-border bg-white px-4 py-2 text-sm font-semibold text-nebula-fg-2 hover:bg-nebula-tint"
          >
            Cancel
          </button>
          <button
            onClick={() => void save()}
            disabled={saving}
            className="rounded-md bg-nebula-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-4">
        {label}
      </div>
      {children}
    </div>
  );
}
